/**
 * TableGrid Component
 * A reusable table component that supports sorting, pagination, row selection, and navigation.
 * Built with Material-UI components and React.
 */

import React, { useEffect, useState } from "react";
import { Table, TableContainer, TableHead, TableRow, TableCell, Checkbox, Box, IconButton, TableBody, CircularProgress, TablePagination } from "@mui/material";
import { TableBackOfficeEndData, TableTitle } from "../TableFunctions";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Link } from "react-router-dom";

/**
 * Interface definitions for the table structure
 */
export interface Column {
    name: string;        // Display name of the column
    sortable: boolean;   // Whether the column can be sorted
    sortName: string;    // Name used for sorting operations
}

export interface Cell {
    value: string       // Content of a table cell
}

export interface Row {
    cells: Cell[]      // Array of cells in the row
    id: number         // Unique identifier for the row
}

/**
 * Props interface for the TableGrid component
 */
export interface GridProps {
    columns: Column[];  // Column definitions
    rows: Row[];       // Data rows
    routerLink?: string;  // Base URL for row navigation
    onRowClick?: (item: Row, event: React.MouseEvent) => void;  // Row click handler
    isLoading?: boolean;  // Loading state indicator
    orderByColumn?: string;  // Current sort column
    ascending?: boolean;     // Sort direction
    onSort?: (column: string, ascending: boolean) => void;  // Sort handler
    showPagination?: boolean;  // Whether to show pagination controls
    totalResults?: number;     // Total number of results
    pageSize?: number;         // Number of rows per page
    currentPage?: number;      // Current page number
    handleChangePage?: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;  // Page change handler
    handleChangeRowsPerPage?: (event: React.ChangeEvent<HTMLInputElement>) => void;  // Page size change handler
    rowsPerPageOptions?: number[];  // Options for rows per page
}

/**
 * TableGrid Component
 * Renders a data table with sorting, pagination, and row selection capabilities
 */
function TableGrid({ columns, rows, isLoading = false, onRowClick, orderByColumn = '', ascending = true, onSort, showPagination = true, totalResults = 0, pageSize = 15, currentPage = 1, handleChangePage, handleChangeRowsPerPage, routerLink, rowsPerPageOptions = [5, 10, 15, 25, 50] }: Readonly<GridProps>): React.JSX.Element {

    // State management for row selection
    const [selectAll, setSelectAll] = useState(false);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);

    // State management for sorting and pagination
    const [orderByColumnState, setOrderByColumnState] = useState(orderByColumn);
    const [ascendingState, setAscendingState] = useState(ascending);
    const [totalResultsState, setTotalResultsState] = useState(totalResults);
    const [pageSizeState, setPageSizeState] = useState(pageSize);
    const [currentPageState, setCurrentPageState] = useState(currentPage);
    const [rowsPerPageOptionsState, setRowsPerPageOptionsState] = useState(rowsPerPageOptions);

    /**
     * Update local state when props change
     */
    useEffect(() => {
        setOrderByColumnState(orderByColumn);
        setAscendingState(ascending);
        setTotalResultsState(totalResults);
        setPageSizeState(pageSize);
        setCurrentPageState(currentPage);
        setRowsPerPageOptionsState(rowsPerPageOptions);
    }, [orderByColumn, ascending, totalResults, pageSize, currentPage, rowsPerPageOptions]);

    /**
     * Toggle select all rows
     */
    const handleSelectAll = () => {
        setSelectAll(!selectAll);
    };

    /**
     * Update selected rows when select all changes
     */
    useEffect(() => {
        if (selectAll) {
            setSelectedRows(rows?.map(row => row.id) || []);
        } else {
            setSelectedRows([]);
        }
    }, [selectAll]);

    /**
     * Check if a row is selected
     */
    const isRowSelected = (id: number) => {
        return selectedRows.includes(id);
    };

    /**
     * Handle individual row selection
     */
    const handleCheckClick = (id: number) => {
        setSelectedRows(prev => {
            if (prev.includes(id)) {
                return prev.filter(rowId => rowId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    /**
     * Get sort icon based on current sort state
     */
    const getSortIcon = (column: string) => {
        if (orderByColumnState !== column) return null;
        return ascendingState ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />;
    };

    /**
     * Handle column sorting
     */
    const handleSort = (column: string) => {
        const newAscending = orderByColumnState === column
            ? !ascendingState
            : true;

        setOrderByColumnState(column);
        setAscendingState(newAscending);

        onSort?.(column, newAscending);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Pagination controls - Fixed at top */}
            {showPagination && (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    mb: 2,
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 2
                }}>
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptionsState}
                        component="div"
                        count={totalResultsState || 0}
                        rowsPerPage={pageSizeState}
                        page={currentPageState - 1}
                        onPageChange={(event, page) => handleChangePage?.(event, page)}
                        onRowsPerPageChange={(event) => handleChangeRowsPerPage?.(event as React.ChangeEvent<HTMLInputElement>)}
                        labelRowsPerPage="Itens por página"
                        labelDisplayedRows={({ from, to, count }) => {
                            const totalPages = Math.ceil(count / pageSizeState);
                            return `${from}-${to} de ${count} (Página ${currentPageState} de ${totalPages})`;
                        }}
                    />
                </Box>
            )}
            
            {/* Table container with fixed height and scrollable body */}
            <TableContainer sx={{ 
                flex: 1,
                maxHeight: 'calc(100vh - 200px)', // Ajuste este valor conforme necessário
                overflow: 'auto'
            }}>
                <Table stickyHeader sx={{ borderBlockEnd: '1px solid rgba(224, 224, 224, 1)' }}>
                    {/* Table header with column titles and sort controls */}
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ 
                                position: 'sticky',
                                top: 0,
                                backgroundColor: 'background.paper',
                                zIndex: 1
                            }}>
                                <Checkbox
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                />
                            </TableCell>
                            {columns.map((column) => (
                                <TableCell 
                                    sx={{ 
                                        paddingBlock: '8px',
                                        position: 'sticky',
                                        top: 0,
                                        backgroundColor: 'background.paper',
                                        zIndex: 1
                                    }} 
                                    key={column.name}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: column.sortable ? 'pointer' : 'default' }} onClick={column.sortable ? () => handleSort(column.sortName) : () => { }}>
                                        <TableTitle title1={column.name} />
                                        {column.sortable && <IconButton size="small" >
                                            {getSortIcon(column.sortName)}
                                        </IconButton>}
                                    </Box>
                                </TableCell>
                            ))}
                            <TableCell sx={{ 
                                paddingBlock: '8px',
                                position: 'sticky',
                                top: 0,
                                backgroundColor: 'background.paper',
                                zIndex: 1
                            }}>
                                <TableTitle title1="" />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    
                    {/* Table body - Scrollable */}
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + 2} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows?.map((row) => (
                                <TableRow 
                                    onClick={(event) => onRowClick?.(row, event)} 
                                    hover={true} 
                                    key={row.id} 
                                    sx={{ cursor: 'pointer', backgroundColor: "none" }}
                                >
                                    <TableCell component="th" scope="row">
                                        <Checkbox
                                            checked={isRowSelected(row.id)}
                                            onClick={() => handleCheckClick(row.id)}
                                        />
                                    </TableCell>
                                    {row.cells.map((cell, cellIndex) => (
                                        <TableCell key={`${row.id}-${cellIndex}`}>{cell.value}</TableCell>
                                    ))}
                                    <TableCell>
                                        <Link to={`${routerLink}/${row.id}`} state={row}>
                                            <TableBackOfficeEndData />
                                        </Link>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default TableGrid;
